import {
  ValidateQRRequest,
  ValidateQRResponse,
  DeviceInfoResponse,
  VerifyPinRequest,
  VerifyPinResponse,
  CallManagerRequest,
  CallManagerResponse,
  VerifyRoomDeliveryPinRequest,
  VerifyRoomDeliveryPinResponse,
  CallTenantRequest,
  CallTenantResponse,
  GetModulesResponse,
} from "./types";
import { getUserLocation } from "./location";

const BASE_API_URL = "https://api.entrioai.com/entrio/api";
//onst BASE_API_URL = "http://localhost:3000/entrio/api";

// 验证QR码并获取session token
export const validateQR = async (
  deviceId: string,
  code: string
): Promise<ValidateQRResponse> => {
  try {
    const location = await getUserLocation();

    const requestData: ValidateQRRequest = {
      deviceId,
      code,
      location,
    };

    const response = await fetch(
      `${BASE_API_URL}/virtual-intercom/user/validate-qr`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      }
    );

    // Try to parse the response body even if the request failed
    const data = await response.json();

    if (!response.ok) {
      // Return the detailed error from the server if available
      return {
        success: false,
        error:
          data.error ||
          data.message ||
          `HTTP error! status: ${response.status}`,
      };
    }

    return data;
  } catch (error) {
    console.error("Error validating QR code:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

// 获取设备信息
export const getDeviceInfo = async (
  sessionToken: string
): Promise<DeviceInfoResponse | null> => {
  try {
    const response = await fetch(
      `${BASE_API_URL}/virtual-intercom/user/device-info`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      console.error(
        "Device info error:",
        data.error || data.message || `HTTP error! status: ${response.status}`
      );
      return null;
    }

    return data;
  } catch (error) {
    console.error("Error getting device info:", error);
    return null;
  }
};

// 验证PIN码 (支持 door, delivery, 和 guest 类型)
export const verifyPin = async (
  sessionToken: string,
  pin: string,
  type: "door" | "delivery" | "guest",
  guestId?: string,
  eventPhoto?: File | Blob
): Promise<VerifyPinResponse> => {
  try {
    let body: FormData | string;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${sessionToken}`,
    };

    // If photo is provided, use FormData for multipart/form-data
    if (eventPhoto) {
      body = new FormData();
      body.append("type", type);
      if (type === "guest") {
        body.append("pinCode", pin);
      } else {
        body.append("pin", pin);
      }
      if (guestId) {
        body.append("guestId", guestId);
      }
      body.append("eventPhoto", eventPhoto, "snapshot.jpg");
      // Don't set Content-Type - browser sets it with boundary
    } else {
      // If no photo, use JSON (original behavior)
      headers["Content-Type"] = "application/json";
      const requestData: VerifyPinRequest = {
        type,
        ...(type === "guest" ? { pinCode: pin } : { pin }),
      };
      body = JSON.stringify(requestData);
    }

    const response = await fetch(
      `${BASE_API_URL}/virtual-intercom/user/verify-pin`,
      {
        method: "POST",
        headers,
        body,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage =
        data.error || data.message || `HTTP error! status: ${response.status}`;

      return {
        success: false,
        error: errorMessage,
        statusCode: response.status,
      };
    }

    return data;
  } catch (error) {
    console.error("Error verifying PIN:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

// 验证房间/快递PIN码 (新接口)
export const verifyRoomDeliveryPin = async (
  sessionToken: string,
  type: "room" | "delivery",
  roomId: string | undefined,
  deliveryId: string | undefined,
  pinCode: string,
  eventPhoto?: File | Blob
): Promise<VerifyRoomDeliveryPinResponse> => {
  try {
    let body: FormData | string;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${sessionToken}`,
    };

    // If photo is provided, use FormData for multipart/form-data
    if (eventPhoto) {
      body = new FormData();
      body.append("type", type);
      if (roomId) {
        body.append("roomId", roomId);
      }
      if (deliveryId) {
        body.append("deliveryId", deliveryId);
      }
      body.append("pinCode", pinCode);
      body.append("eventPhoto", eventPhoto, "snapshot.jpg");
      // Don't set Content-Type - browser sets it with boundary
    } else {
      // If no photo, use JSON (original behavior)
      headers["Content-Type"] = "application/json";
      const requestData: VerifyRoomDeliveryPinRequest = {
        type,
        roomId,
        deliveryId,
        pinCode,
      };
      body = JSON.stringify(requestData);
    }

    const response = await fetch(
      `${BASE_API_URL}/virtual-intercom/user/verify-pin`,
      {
        method: "POST",
        headers,
        body,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage =
        data.error || data.message || `HTTP error! status: ${response.status}`;

      return {
        success: false,
        result: {
          success: false,
        },
        error: errorMessage,
        statusCode: response.status,
      };
    }

    return data;
  } catch (error) {
    console.error("Error verifying room/delivery PIN:", error);
    return {
      success: false,
      result: {
        success: false,
      },
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

// 呼叫Manager
export const callManager = async (
  sessionToken: string,
  eventPhoto?: File | Blob
): Promise<CallManagerResponse> => {
  try {
    let body: FormData | undefined;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${sessionToken}`,
    };

    // If photo is provided, use FormData for multipart/form-data
    if (eventPhoto) {
      body = new FormData();
      body.append("eventPhoto", eventPhoto, "snapshot.jpg");
    } else {
      // If no photo, use JSON (original behavior)
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(
      `${BASE_API_URL}/virtual-intercom/user/call_manager`,
      {
        method: "POST",
        headers,
        body,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage =
        data.error || data.message || `HTTP error! status: ${response.status}`;

      return {
        success: false,
        error: errorMessage,
        statusCode: response.status,
      };
    }

    return data;
  } catch (error) {
    console.error("Error calling manager:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

// 呼叫Tenant
export const callTenant = async (
  sessionToken: string,
  roomId: string,
  eventPhoto?: File | Blob,
  userIds?: string[]
): Promise<CallTenantResponse> => {
  try {
    let body: FormData | string;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${sessionToken}`,
    };

    // If photo is provided, use FormData for multipart/form-data
    if (eventPhoto) {
      const formData = new FormData();
      formData.append("roomId", roomId);
      formData.append("eventPhoto", eventPhoto, "snapshot.jpg");
      // Add user_ids if provided
      // Use bracket notation to send as array: user_ids[]
      if (userIds && userIds.length > 0) {
        userIds.forEach((userId) => {
          formData.append("user_ids[]", userId);
        });
      }
      body = formData;
      // Don't set Content-Type - browser sets it with boundary
    } else {
      // If no photo, use JSON (original behavior)
      headers["Content-Type"] = "application/json";
      const requestData: CallTenantRequest = {
        roomId,
        ...(userIds && userIds.length > 0 && { user_ids: userIds }),
      };
      body = JSON.stringify(requestData);
    }

    const response = await fetch(
      `${BASE_API_URL}/virtual-intercom/user/call_tenant`,
      {
        method: "POST",
        headers,
        body,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage =
        data.error || data.message || `HTTP error! status: ${response.status}`;

      return {
        success: false,
        error: errorMessage,
        statusCode: response.status,
      };
    }

    return data;
  } catch (error) {
    console.error("Error calling tenant:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

// 结束会话
export const endSession = async (
  sessionToken: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const response = await fetch(
      `${BASE_API_URL}/virtual-intercom/user/end-session`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error:
          data.error ||
          data.message ||
          `HTTP error! status: ${response.status}`,
      };
    }

    return data;
  } catch (error) {
    console.error("Error ending session:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

// 获取模块列表
export const getModules = async (
  sessionToken: string
): Promise<GetModulesResponse> => {
  try {
    const response = await fetch(
      `${BASE_API_URL}/virtual-intercom/user/modules`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        modules: [],
        error:
          data.error ||
          data.message ||
          `HTTP error! status: ${response.status}`,
      };
    }

    return data;
  } catch (error) {
    console.error("Error getting modules:", error);
    return {
      success: false,
      modules: [],
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};
